import HCLTreeMxGraphEncoder from './HCLTreeMxGraphEncoder';
import EventTreeMxGraph, {EventTreeMxGraphJSON} from '../HCLTreeMxGraph/EventTreeMxGraph';
import ReferenceTypes from "../HCLTreeNode/HCLTreeVertexValue/ReferenceTypes";
import Outcome from "../HCLTreeNode/HCLTreeVertexValue/Outcome";
import EventTreeVertex from "../HCLTreeNode/EventTreeNode/EventTreeVertex";
import InitVertex from "../HCLTreeNode/EventTreeNode/InitEvent/InitVertex";
import EndStateVertex from "../HCLTreeNode/EventTreeNode/EndStateNode/EndStateVertex";
import {HCLTreeVertexValueJSON} from "../HCLTreeNode/HCLTreeVertexValue/HCLTreeVertexValue";
import FunctionalEventVertex from "../HCLTreeNode/EventTreeNode/FunctionalEventNode/FunctionalEventVertex";
import FunctionalEventVertexValue from "../HCLTreeNode/EventTreeNode/FunctionalEventNode/FunctionalEventVertexValue";
import InitVertexValue from "../HCLTreeNode/EventTreeNode/InitEvent/InitVertexValue";
import FunctionalEventPortValue from "../HCLTreeNode/EventTreeNode/FunctionalEventNode/Port/FunctionalEventPortValue";
import EventTreeTransferGateVertex from "../HCLTreeNode/EventTreeNode/EventTreeTransferGateNode/EventTreeTransferGateVertex";

/**
 * Inherits from {@link HCLTreeMxGraphEncoder} and implements encoding for {@link EventTreeMxGraph}
 */
class EventTreeMxGraphEncoder extends HCLTreeMxGraphEncoder {

  private readonly branchKeys: Set<string>;

  constructor(graph: EventTreeMxGraph) {
    super(graph);
    this.branchKeys = new Set<string>();
  }

  encode(): EventTreeMxGraphJSON {

    const json: EventTreeMxGraphJSON = {
      [ReferenceTypes.BRANCHES]: {},
      [ReferenceTypes.FUNCTIONAL_EVENTS]: {},
      [ReferenceTypes.INITIAL_STATE]: {},
      [ReferenceTypes.SEQUENCES]: {},
      name: this.getGraph().getTreeLabel().getName(),
      model_tree_id: this.getGraph().getModelTreeId(),
      label: this.getGraph().getTreeLabel().toJSON()
    };

    const vertices = this.getGraph().getVertices() as EventTreeVertex[];

    const initVertices = vertices.filter(vertex => vertex instanceof InitVertex);
    if (initVertices.length !== 1) throw new Error("Only one InitVertex is allowed in Event Tree");

    vertices.forEach(vertex => {
      if (vertex instanceof InitVertex) {
        if (vertex.edges && vertex.edges[0]) {
          (vertex.getValue() as InitVertexValue).setOutcome(Outcome.build((vertex.edges[0].target as FunctionalEventVertex).toReferenceJSON()));
        } else {
          (vertex.getValue() as InitVertexValue).setOutcome(null);
        }
        json[ReferenceTypes.INITIAL_STATE] = vertex.toJSON();
      }
      else if (vertex instanceof EndStateVertex) {
        json[ReferenceTypes.SEQUENCES][vertex.getKey()] = vertex.toJSON() as HCLTreeVertexValueJSON;
      }
      else if (vertex instanceof FunctionalEventVertex) {
        json[ReferenceTypes.FUNCTIONAL_EVENTS][vertex.getKey()] = vertex.toJSON() as HCLTreeVertexValueJSON;

        // update port values before calling toBranchJSON()
        const successPortValue = vertex.successPort.getValue() as FunctionalEventPortValue;
        successPortValue.setOutcome((vertex.successPort.edges && vertex.successPort.edges[0] && vertex.successPort.edges[0].target) ? Outcome.build((vertex.successPort.edges[0].target as EndStateVertex | FunctionalEventVertex).toReferenceJSON()) : null);

        const failurePortValue = vertex.failurePort.getValue() as FunctionalEventPortValue;
        failurePortValue.setOutcome((vertex.failurePort.edges && vertex.failurePort.edges[0] && vertex.failurePort.edges[0].target) ? Outcome.build((vertex.failurePort.edges[0].target as EndStateVertex | FunctionalEventVertex).toReferenceJSON()) : null);

        const value = vertex.getValue() as FunctionalEventVertexValue;
        if (value.getBranchKey()) {
          this.branchKeys.add(value.getBranchKey());
        } else {
          value.setBranchKey((this.getGraph() as EventTreeMxGraph).generateUniqueBranchKey());
        }
        json[ReferenceTypes.BRANCHES][value.getBranchKey()] = vertex.toBranchJSON();
      }
      else if (vertex instanceof EventTreeTransferGateVertex) {
        throw new Error("Encoding of EventTreeTransferGateVertex not supported");
      }
    });

    return json;
  }
}

export default EventTreeMxGraphEncoder;
